// ─── local preferences ───────────────────────────────────────────────────────
// Everything the boss chose last time: provider, persona, turn-taking, which face,
// and the .vrm they picked. Settings are small and JSON, so localStorage; a model is
// megabytes of binary, so IndexedDB. The model itself is NEVER bundled and never
// leaves the browser — the licence for a VRM is not ours to redistribute.

import type { TurnTaking } from '@shared/protocol.ts'

export interface Settings {
  provider: string
  /**
   * True once the user has chosen a provider themselves. Until then the app follows
   * the service's catalog, so pasting a key into the Worker is all it takes to be
   * talking to that provider — no setting to find, no rebuild.
   */
  pinned: boolean
  /** `''` = take the provider's own default placement. */
  placement: '' | 'server' | 'client'
  persona: string
  greeting: string
  voice: string
  turnTaking: TurnTaking
  face: string
  /** Base URL for a client-placed local stack (LM Studio, MiniCPM-o, whisper). */
  localBaseUrl: string
  /** Display name of the stored model, so the picker can say what is loaded. */
  modelName: string
}

export const DEFAULT_SETTINGS: Settings = {
  provider: 'local-pipeline',
  pinned: false,
  placement: '',
  persona: 'You are Vox, a warm and very concise voice assistant. Answer in one or two sentences. Never read out URLs.',
  greeting: 'Vox here. What are we doing?',
  // Empty on purpose: each provider has its own voice names, and sending one
  // provider's name to another is a needless way to fail a first session.
  voice: '',
  turnTaking: 'vad',
  face: 'orb',
  localBaseUrl: 'http://localhost:1234/v1',
  modelName: '',
}

const KEY = 'voicebox.settings.v1'

export interface Loaded {
  settings: Settings
  /**
   * Has this browser ever run the app? The defaults above are ours, not anyone's
   * preference, and the app writes them to storage during boot — so without this
   * flag a first load cannot tell "the boss chose this" from "we invented it a
   * moment ago", and ends up warning a new visitor about their own saved settings.
   */
  returning: boolean
}

export function loadSettings(): Loaded {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { settings: { ...DEFAULT_SETTINGS }, returning: false }
    // Merged over the defaults so a new setting doesn't need a migration.
    return { settings: { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) }, returning: true }
  } catch {
    return { settings: { ...DEFAULT_SETTINGS }, returning: false }
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings))
  } catch {
    // private mode / quota — the app is still perfectly usable, just forgetful
  }
}

// ── the user's model ─────────────────────────────────────────────────────────
const DB = 'voicebox'
const STORE = 'model'

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('indexedDB unavailable'))
  })
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const req = run(db.transaction(STORE, mode).objectStore(STORE))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error ?? new Error('indexedDB write failed'))
      }),
  )
}

export async function saveModel(file: Blob): Promise<void> {
  await tx('readwrite', (s) => s.put(file, 'vrm'))
}

export async function loadModel(): Promise<Blob | null> {
  return (await tx<Blob | undefined>('readonly', (s) => s.get('vrm'))) ?? null
}

export async function clearModel(): Promise<void> {
  await tx('readwrite', (s) => s.delete('vrm'))
}
